import {
  Alert,
  AlertDescription,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text as ChakraText,
  useToast,
  VStack,
} from '@chakra-ui/react'
import { useState, useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import type { UserSessionResponse } from '../../interfaces'

interface Props {
  isOpen: boolean
  onClose(): void
  user: UserSessionResponse | null
  selectedPlanId: string
}

const formatLimitsError = (errorMsgs: string[]): string | null => {
  const lines = errorMsgs.map(v => {
    return `• ${v}`
  })

  return [
    `You can't switch to this plan. Please check the below error(s):\n`,
    ...lines,
    `\nIf this is a mistake, please contact support.`,
  ].join('\n')
}

const PlanChangeModal = ({
  isOpen,
  onClose,
  user,
  selectedPlanId,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { request } = useFetch({ cachePolicy: CachePolicies.NO_CACHE })

  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  /* 🔄 Reset state whenever the modal opens */
  useEffect(() => {
    if (isOpen) {
      setError(null)
      setIsSubmitting(false)
    }
  }, [isOpen])

  const handleClose = () => {
    setError(null)
    setIsSubmitting(false)
    onClose()
  }

  const handleConfirm = async () => {
    if (!user) return

    setIsSubmitting(true)
    setError(null)

    const resp = await request.patch(
      `/api/billing/change-plan/${user.clientNumber}`,
      { selectedPlanId },
    )

    setIsSubmitting(false)

    if (request.error) {
      if (resp?.reason === 'CUSTOM_ERRORS' && resp?.errorMsgs) {
        setError(formatLimitsError(resp.errorMsgs))
      } else {
        setError(
          resp?.message ??
          t(
            'something_went_wrong',
            'Something went wrong while changing plan.',
          ),
        )
      }
      return
    }

    toast({
      title: t(
        'plan_changed',
        'Plan changed successfully. Taking you to subscription page.',
      ),
      status: 'success',
      isClosable: true,
      duration: 2000,
      position: 'top-right',
      variant: 'outline',
    })
    handleClose()
    setTimeout(() => {
      window.location.href = '/dashboard/billing/subscriptions'
    }, 2000)
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('confirm_plan_change', 'Confirm Plan Change')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            {error && (
              <Alert status="error" minWidth="unset">
                <AlertDescription whiteSpace="pre-line">
                  {error}
                </AlertDescription>
              </Alert>
            )}
            <ChakraText>
              The selected plan will be applied immediately, and its limits will take effect right away. Your next invoice will reflect the pricing of the new plan. Be sure to compare the limits before proceeding.
            </ChakraText>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid #e7eaf0" gap="1em">
          <Button onClick={handleClose} variant="outline">
            {t('cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
          >
            {t('confirm', 'Confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PlanChangeModal
